/*
  Recently viewed.

  Two jobs. It records the handle of any product page as it is visited, and
  on pages carrying the section it rebuilds a row from that history.

  Cards are fetched one per product through the Section Rendering API rather
  than built here, so there is a single definition of a product card in the
  theme. The alternative, rendering cards in JavaScript, means the markup
  drifts out of step with card-product.liquid the first time either changes.
*/
(() => {
  const KEY = 'recently-viewed';
  const MAX_STORED = 20;

  // Read from the path rather than the page markup, so recording does not
  // depend on the product template exposing anything.
  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  const currentHandle = match ? match[1] : null;

  const read = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return [];
    }
  };

  const write = (handles) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(handles));
    } catch (error) {
      // Private browsing or storage disabled. Recording is optional.
    }
  };

  if (currentHandle) {
    const handles = read().filter((handle) => handle !== currentHandle);
    handles.unshift(currentHandle);
    write(handles.slice(0, MAX_STORED));
  }

  if (customElements.get('recently-viewed')) return;

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.limit = Number(this.dataset.limit) || 8;
      this.load();
    }

    async load() {
      // The product being looked at is excluded: it is not something the
      // shopper needs reminding of.
      const handles = read()
        .filter((handle) => handle !== currentHandle)
        .slice(0, this.limit);

      if (!handles.length) return;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cards = await Promise.all(handles.map((handle) => this.fetchCard(root, handle)));
      const html = cards.filter(Boolean).join('');

      if (!html) return;

      this.track.innerHTML = html;

      // Stays out of the document until there is something to show, so an
      // empty heading never appears for a first time visitor.
      this.removeAttribute('hidden');
      this.dispatchEvent(new CustomEvent('recently-viewed:loaded', { bubbles: true }));
    }

    async fetchCard(root, handle) {
      const params = new URLSearchParams({ section_id: 'recently-viewed-card' });

      // Without this the request is served by the published theme, which on
      // an unpublished theme means this section does not exist there and
      // nothing ever comes back.
      const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) params.set('preview_theme_id', preview);

      try {
        const response = await fetch(`${root}products/${handle}?${params}`);

        // A deleted or unpublished product returns a 404. Skip it quietly.
        if (!response.ok) return '';

        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const card = parsed.querySelector('[data-card]');

        return card ? card.outerHTML : '';
      } catch (error) {
        return '';
      }
    }
  }

  customElements.define('recently-viewed', RecentlyViewed);
})();
